// National seats-per-party rollup. Seats accrue as districts complete
// (or partially — every elected seat across all 13 districts is
// counted regardless of district completion).
//
// Reads through useResultsData so demo mode + real mode share the
// same component. Banner is rendered once at the page level — no
// inline DEMO chip here (it would be a third banner on the page).

import { useMemo } from 'react';
import {
  DISTRICT_NUMBERS,
  deriveDistrictCount,
} from '../../data/electionday-results-types';
import { ALL_PARTIES, type CanonicalParty } from '../../../api/_lib/parties';
import { useResultsRoster, partyColour, partyChip } from './useResultsRoster';
import { useResultsData } from './useResultsData';
import './Results.css';

export default function NationalRollup() {
  const { data, isDemo } = useResultsData();
  const { byId, byDistrict } = useResultsRoster();

  const seatsByParty = useMemo(() => {
    const tally: Partial<Record<CanonicalParty, number>> = {};
    for (const n of DISTRICT_NUMBERS) {
      const candidateIds = byDistrict(n).map((c) => c.id);
      const derived = deriveDistrictCount(n, data.districts[String(n)], candidateIds);
      for (const id of derived.electedSeats) {
        const cand = byId(id);
        if (!cand) continue;
        tally[cand.party] = (tally[cand.party] ?? 0) + 1;
      }
    }
    return tally;
  }, [data, byDistrict, byId]);

  const totalElected = Object.values(seatsByParty).reduce(
    (a, b) => (a ?? 0) + (b ?? 0),
    0,
  ) ?? 0;
  const TOTAL_SEATS = 65; // 13 districts × 5 seats (casual + bonus seats out of scope here)

  return (
    <section className="results-card" aria-label="National seat rollup">
      {isDemo && (
        <p className="results-demo-inline">⚠ Demo / Test Data</p>
      )}
      <p className="results-card__kicker">National rollup</p>
      <h3 className="results-card__title">Seats elected</h3>
      <p className="results-card__sub">
        {totalElected} of {TOTAL_SEATS} district seats filled
      </p>
      <div className="rollup">
        {ALL_PARTIES.map((p) => {
          const count = seatsByParty[p] ?? 0;
          if (count === 0) return null;
          return (
            <div
              key={p}
              className="rollup__chip"
              style={{ borderLeftColor: partyColour(p) }}
            >
              <p className="rollup__party">{partyChip(p)}</p>
              <p className="rollup__seats">{count}</p>
            </div>
          );
        })}
        {totalElected === 0 && (
          <p className="results-empty">No seats elected yet.</p>
        )}
      </div>
    </section>
  );
}
